import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MapPin, ChevronLeft, ChevronRight, Carrot, Clock, Check } from "lucide-react";
import { DONUTS, getAvailability, getTruckStatus, isJuly4DropActive, JULY4_GRADIENT } from "@/data/content";
import { useSchedule } from "@/hooks/useSchedule";
import { ConfettiBurst } from "@/components/ConfettiBurst";
import { Fireworks } from "@/components/Fireworks";

const scrollTo = (id) =>
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

// Vegan callouts temporarily hidden (flip to re-surface carrot icons + note)
const SHOW_VEGAN = false;

export const Donuts = () => {
  const [active, setActive] = useState(0);
  const [burst, setBurst] = useState(0);
  const locations = useSchedule();
  // Only flavours without `hidden: true` are surfaced (flags live in content.js)
  const july4Active = isJuly4DropActive();
  // Only surface non-hidden flavours; retire seasonal drops after their end date.
  const donuts = DONUTS.filter(
    (dn) => !dn.hidden && !(dn.seasonal === "july4" && !july4Active)
  );
  const d = donuts[active];
  const avail = d.availableAt ? getAvailability(d.availableAt, locations) : null;
  const truck = d.availableAt ? null : getTruckStatus(locations);

  const next = () => setActive((a) => (a + 1) % donuts.length);
  const prev = () => setActive((a) => (a - 1 + donuts.length) % donuts.length);

  // pop confetti whenever the featured flavour changes
  useEffect(() => {
    setBurst((b) => b + 1);
  }, [active]);

  return (
    <section
      id="donuts"
      data-testid="donuts-section"
      className="relative py-20 md:py-28 px-5 md:px-10 overflow-hidden bg-[#f7f0eb]"
    >
      <AnimatePresence>
        <motion.div
          key={active}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.8 }}
          className="pointer-events-none absolute -top-24 -right-24 w-[42rem] h-[42rem] rounded-full blur-[120px]"
          style={{ background: d.heroColor || d.color, opacity: 0.25 }}
        />
      </AnimatePresence>

      <div className="relative max-w-7xl mx-auto">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <h2 className="font-display font-bold text-4xl sm:text-6xl text-[#ff648b] tracking-tight leading-[0.95]">
              This Week's Lineup
            </h2>
            <p className="mt-3 inline-flex items-center gap-2 text-[#3d3d3d] font-medium">
              <span className="inline-block w-2 h-2 rounded-full bg-[#ed4a76]" />
              Every Bobo's donut is hand-cut and made fresh.
            </p>
          </div>
        </div>

        <div className="mt-10 grid lg:grid-cols-12 gap-x-2 gap-y-8 items-center">
          {/* featured copy */}
          <div className="lg:col-span-6 order-2 lg:order-1 text-center lg:text-right flex flex-col lg:items-end">
            <AnimatePresence mode="wait">
              <motion.div
                key={active}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                transition={{ duration: 0.35 }}
                className="lg:flex lg:flex-col lg:items-end"
              >
                <span
                  className={`inline-block px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wide shadow-sm ${
                    d.seasonal === "july4"
                      ? "text-white ring-1 ring-white/40"
                      : "text-[#111111]"
                  }`}
                  style={{ background: d.seasonal === "july4" ? JULY4_GRADIENT : d.color }}
                >
                  {d.tag}
                </span>
                <h3
                  className={`font-display font-bold text-4xl sm:text-5xl mt-5 leading-[1.05] ${
                    d.seasonal === "july4" ? "text-transparent bg-clip-text" : "text-[#111111]"
                  }`}
                  style={d.seasonal === "july4" ? { backgroundImage: JULY4_GRADIENT } : undefined}
                >
                  {d.line1 && d.line2 ? (
                    <span className="flex flex-col items-center lg:items-end gap-1">
                      <span>{d.line1}</span>
                      <span className="inline-flex items-center gap-3 flex-wrap justify-center lg:justify-end">
                        {d.line2}
                        {SHOW_VEGAN && d.vegan && (
                          <Carrot
                            data-testid="vegan-icon-name"
                            className="h-7 w-7 text-[#7bd389] shrink-0"
                            aria-label="Vegan option available"
                          />
                        )}
                      </span>
                    </span>
                  ) : d.name.includes("Donut Holes") ? (
                    <span className="flex flex-col items-center lg:items-end gap-1">
                      <span>{d.name.replace(" Donut Holes", "")}</span>
                      <span className="inline-flex items-center gap-3 flex-wrap justify-center lg:justify-end">
                        Donut Holes
                        {SHOW_VEGAN && d.vegan && (
                          <Carrot
                            data-testid="vegan-icon-name"
                            className="h-7 w-7 text-[#7bd389] shrink-0"
                            aria-label="Vegan option available"
                          />
                        )}
                      </span>
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-3 flex-wrap justify-center lg:justify-end">
                      {d.name}
                      {SHOW_VEGAN && d.vegan && (
                        <Carrot
                          data-testid="vegan-icon-name"
                          className="h-7 w-7 text-[#7bd389] shrink-0"
                          aria-label="Vegan option available"
                        />
                      )}
                    </span>
                  )}
                </h3>
                <p className="text-lg text-[#3d3d3d] mt-4 leading-relaxed max-w-md mx-auto lg:mx-0">
                  {d.desc}
                </p>
                {SHOW_VEGAN && d.vegan && (
                  <p
                    data-testid="vegan-note"
                    className="text-sm font-semibold text-[#5d9c5d] mt-2 inline-flex items-center gap-1.5 lg:justify-end"
                  >
                    <Carrot className="h-4 w-4 text-[#7bd389]" /> = Vegan Option
                  </p>
                )}
                {(avail || truck) && (
                  <span
                    data-testid="donut-availability"
                    className={`mt-4 inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-sm font-bold shadow-sm ${
                      avail?.availableToday || truck?.openNow
                        ? "bg-[#7bd389] text-[#1f3d22]"
                        : d.availableAt
                        ? "bg-[#c9b6e0]/40 text-[#5b4a7a]"
                        : "bg-[#111111]/10 text-[#3d3d3d]"
                    }`}
                  >
                    {d.availableAt ? (
                      avail.availableToday ? (
                        <>
                          <MapPin className="h-4 w-4" /> Available today at {avail.place}
                        </>
                      ) : (
                        <>
                          <Clock className="h-4 w-4" /> {d.availability} — back in {avail.daysUntil}{" "}
                          {avail.daysUntil === 1 ? "day" : "days"}
                        </>
                      )
                    ) : truck.openNow ? (
                      <>
                        <Check className="h-4 w-4" /> Available on the truck NOW
                      </>
                    ) : (
                      <>
                        <Clock className="h-4 w-4" /> Available on the truck {truck.phrase}
                      </>
                    )}
                  </span>
                )}
              </motion.div>
            </AnimatePresence>
            <button
              data-testid="donuts-find-truck-btn"
              onClick={() => scrollTo("locations")}
              className="mt-8 inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#111111] text-[#f7f0eb] font-bold hover:bg-[#000000] hover:scale-105 transition-all shadow-lg"
            >
              <MapPin className="h-5 w-5" /> Find these at the truck
            </button>
          </div>

          {/* featured donut stage */}
          <div
            className="lg:col-span-6 order-1 lg:order-2 relative flex items-center justify-center min-h-[20rem] sm:min-h-[26rem]"
            onMouseEnter={() => setBurst((b) => b + 1)}
          >
            {/* mobile arrows */}
            <button
              data-testid="donut-prev-btn"
              onClick={prev}
              aria-label="Previous donut"
              className="sm:hidden absolute left-0 top-1/2 -translate-y-1/2 z-30 grid place-items-center w-11 h-11 rounded-full bg-[#111111] text-[#f7f0eb] shadow-lg active:scale-90 transition-transform"
            >
              <ChevronLeft className="h-6 w-6" />
            </button>
            <button
              data-testid="donut-next-btn"
              onClick={next}
              aria-label="Next donut"
              className="sm:hidden absolute right-0 top-1/2 -translate-y-1/2 z-30 grid place-items-center w-11 h-11 rounded-full bg-[#111111] text-[#f7f0eb] shadow-lg active:scale-90 transition-transform"
            >
              <ChevronRight className="h-6 w-6" />
            </button>
            {/* colourful animated rings */}
            <div
              className="absolute w-[19rem] h-[19rem] sm:w-[24rem] sm:h-[24rem] rounded-full animate-spin-slow z-0"
              style={{
                background:
                  "conic-gradient(from 0deg, #ff648b, #ffd166, #7bd389, #5bc0eb, #c77dff, #ed4a76, #ff648b)",
                WebkitMask:
                  "radial-gradient(farthest-side, transparent calc(100% - 11px), #000 calc(100% - 11px))",
                mask: "radial-gradient(farthest-side, transparent calc(100% - 11px), #000 calc(100% - 11px))",
                opacity: 0.85,
              }}
            />
            <div
              className="absolute w-[15.5rem] h-[15.5rem] sm:w-[20.5rem] sm:h-[20.5rem] rounded-full border-[3px] border-dashed border-[#ed4a76]/60 animate-spin-rev z-0"
            />
            <div
              className="absolute w-[22rem] h-[22rem] sm:w-[27rem] sm:h-[27rem] rounded-full border-2 border-dotted border-[#5bc0eb]/40 animate-spin-fast z-0"
            />
            <AnimatePresence mode="wait">
              <motion.div
                key={"disc" + active}
                initial={{ scale: 0.7, opacity: 0 }}
                animate={{ scale: 1, opacity: 0.9 }}
                exit={{ scale: 0.7, opacity: 0 }}
                transition={{ duration: 0.4 }}
                className="absolute w-[15rem] h-[15rem] sm:w-[20rem] sm:h-[20rem] rounded-full z-0"
                style={{ background: `radial-gradient(circle, ${d.heroColor || d.color}, ${d.heroColor || d.color}00 72%)` }}
              />
            </AnimatePresence>

            {/* patriotic fireworks behind the featured 4th-of-July drop */}
            {d.fireworks && <Fireworks />}

            {/* confetti / rings explode under the donut */}
            <ConfettiBurst key={burst} count={34} />

            <AnimatePresence mode="wait">
              <motion.img
                key={d.img}
                src={d.img}
                alt={d.name}
                data-testid="donut-featured-img"
                onMouseEnter={() => setBurst((b) => b + 1)}
                initial={{ opacity: 0, scale: 0.6, rotate: -25 }}
                animate={{ opacity: 1, scale: 1, rotate: 0, y: [0, -14, 0] }}
                exit={{ opacity: 0, scale: 0.6, rotate: 25 }}
                whileHover={{
                  scale: 1.25,
                  rotate: [0, -8, 8, -5, 5, 0],
                  transition: {
                    rotate: { duration: 0.6, ease: "easeInOut" },
                    scale: { type: "spring", stiffness: 280, damping: 11 },
                  },
                }}
                whileTap={{ scale: 0.95 }}
                transition={{
                  opacity: { duration: 0.35 },
                  scale: { duration: 0.45, type: "spring", bounce: 0.4 },
                  rotate: { duration: 0.45 },
                  y: { duration: 5, repeat: Infinity, ease: "easeInOut" },
                }}
                className="relative z-10 w-[16rem] sm:w-[22rem] cursor-pointer drop-shadow-[0_30px_30px_rgba(62,39,35,0.3)] hover:drop-shadow-[0_26px_44px_rgba(232,138,160,0.55)] transition-[filter] duration-300"
              />
            </AnimatePresence>
          </div>
        </div>

        {/* mobile dots indicator */}
        <div className="mt-8 flex sm:hidden justify-center gap-2">
          {donuts.map((dn, i) => (
            <button
              key={dn.name}
              data-testid={`donut-dot-${i}`}
              onClick={() => setActive(i)}
              aria-label={dn.name}
              className={`h-2.5 rounded-full transition-all ${
                active === i ? "w-7 bg-[#111111]" : "w-2.5 bg-[#111111]/25"
              }`}
            />
          ))}
        </div>

        {/* thumbnail selector (tablet & up) — single row, holes included */}
        <div className="mt-10 hidden sm:flex flex-wrap justify-center items-center gap-5 lg:gap-7">
          {donuts.map((dn, i) => {
            const avF = dn.availableAt ? getAvailability(dn.availableAt, locations) : null;
            const avT = dn.availableAt ? null : getTruckStatus(locations);
            return (
            <button
              key={dn.name}
              data-testid={`donut-thumb-${i}`}
              onMouseEnter={() => setActive(i)}
              onClick={() => setActive(i)}
              aria-label={dn.name}
              className="group relative flex items-center justify-center focus:outline-none"
            >
              <span
                data-testid={`donut-thumb-label-${i}`}
                className="pointer-events-none absolute -top-9 left-1/2 -translate-x-1/2 whitespace-nowrap px-2.5 py-1 rounded-full bg-[#111111] text-[#f7f0eb] text-xs font-semibold opacity-0 group-hover:opacity-100 transition-opacity duration-200 shadow-md z-20"
              >
                {dn.name}
              </span>
              <span
                className={`relative grid place-items-center w-20 h-20 lg:w-24 lg:h-24 rounded-full transition-all duration-300 ${
                  active === i
                    ? "ring-4 ring-[#111111] scale-110 shadow-lg"
                    : "ring-2 ring-transparent opacity-70 group-hover:opacity-100 group-hover:scale-105"
                }`}
                style={{ background: `${dn.color}55` }}
              >
                {(avF || avT) && (
                  <span
                    data-testid={`donut-thumb-avail-${i}`}
                    title={
                      dn.availableAt
                        ? avF.availableToday
                          ? `Available today at ${avF.place}`
                          : `Back at ${avF.place} in ${avF.daysUntil} ${avF.daysUntil === 1 ? "day" : "days"}`
                        : avT.openNow
                        ? "Available on the truck NOW"
                        : `Available on the truck ${avT.phrase}`
                    }
                    className={`absolute -top-1 -right-1 z-10 inline-flex items-center justify-center rounded-full shadow-md ring-2 ring-[#f7f0eb] ${
                      avF?.availableToday || avT?.openNow
                        ? "h-5 w-5 bg-[#7bd389] text-[#1f3d22]"
                        : "h-5 px-1.5 bg-[#111111] text-[#f7f0eb]"
                    }`}
                  >
                    {dn.availableAt ? (
                      avF.availableToday ? (
                        <MapPin className="h-3 w-3" />
                      ) : (
                        <span className="inline-flex items-center gap-0.5 text-[10px] font-extrabold leading-none">
                          <Clock className="h-3 w-3" />
                          {avF.daysUntil}d
                        </span>
                      )
                    ) : avT.openNow ? (
                      <Check className="h-3 w-3" />
                    ) : (
                      <span className="inline-flex items-center gap-0.5 text-[10px] font-extrabold leading-none">
                        <Clock className="h-3 w-3" />
                        {avT.daysUntil >= 1 ? `${avT.daysUntil}d` : ""}
                      </span>
                    )}
                  </span>
                )}
                <img
                  src={dn.img}
                  alt={dn.name}
                  className="w-[78%] h-[78%] object-contain drop-shadow-[0_6px_8px_rgba(62,39,35,0.25)] donut-wobble"
                />
              </span>
            </button>
            );
          })}
        </div>
      </div>
    </section>
  );
};
